// The BLOCKER FIX LINK — a signed, short-lived, WRITE-ONLY capability to fill one
// blocker's form, minted so an agent can hand a human (or the human's own agent)
// a URL that opens a form ALREADY LISTING exactly what the blocked work needs.
//
//   https://api.developerz.ai/blocker-fix/<jwt>
//
// WHY THIS EXISTS. A coding run is non-interactive by construction: it holds a
// credential set sealed at claim time and cannot pause, ask for a key, and
// resume. So a run that needs a secret nobody supplied dies — 971 of 1,853 failed
// production tasks carry a NULL `failure_kind`. `blockers` is the object built to
// catch that, but answering one in prose ("the token is in 1Password") still
// leaves a human to go and paste it somewhere. This link closes the last gap: the
// same act supplies the value and answers the blocker.
//
// WHAT IT IS NOT. It is not an authentication token, not a session, and not a
// read capability. It can fill the fields of exactly one blocker and it can NEVER
// read a stored value back — every credential in this system is
// write-once/read-never, there is no re-reveal door anywhere, and this is not
// going to be the first one.
//
// WHAT IT CARRIES (v2, epic #3756 door 5): the tenant, the blocker, and
// `fields_hash` — the sha256 of the blocker's sorted field NAMES ({@link fieldsHash}).
// Never a value, and no longer the names themselves. The payload is base64url, NOT
// encryption: anyone holding the link can read every claim in it, and a value in
// here would be a secret published in a URL.
//
// WHY A HASH AND NOT THE FIELDS. A v2 form spans kinds and environments (an env
// secret for `dev`, a service URL for `ci`, an OAuth connection), so there is no
// one `(repo, environment)` for the token to name; the redeem reads the field
// specs from the blocker row. What the SIGNATURE still has to guarantee is the v1
// property: a row re-raised with MORE fields between mint and click cannot widen
// a link already in someone's inbox. The hash is that guarantee — the redeem
// recomputes {@link needsFieldsHash} over the row and refuses a mismatch. It
// covers the field SET, not `landed`, so a partially filled form keeps its link.
//
// Redemption discipline (GET renders / POST performs, path-borne JWT as the
// anti-CSRF token, the CAS before the side effect) lives in
// `apps/api/src/routes/blocker-fix.ts` — read
// that module's header for the reasoning, which is not repeated here. Write-once
// per field is `landed`'s rule (`entities/blocker/needs-form.ts`), enforced where
// the submit stores it.
//
// Namespace import: see the note in `./jws` (browser-evaluability, #180).
import * as nodeCrypto from 'node:crypto';
import { z } from 'zod';
import {
  type Blocker,
  BlockerKind,
  type BlockerNeeds,
  isBlockerAskableEnvironment,
  NeedsFieldKind,
  needsFieldNames,
} from '../entities/blocker';
import { Scope } from '../scopes';
import { AccountIdSchema, BlockerIdSchema } from './ids';
import { encodeHeader, encodeJson, openCompactJws, signSegments } from './jws';
import { CredentialExposure } from './project-ingress';

/**
 * ONE HOUR, and deliberately short.
 *
 * THIS link WRITES A CREDENTIAL into a tenant's vault, and it travels through the places an agent's output goes —
 * a chat transcript, a Slack channel, an email, a run log. All of those are
 * durable, and a capability that outlives the conversation it was minted in is a
 * capability sitting in a searchable archive.
 *
 * The cost of being wrong on the short side is one extra call: the link is minted
 * ON READ in the `blocker list` projection, so an expired one is replaced by
 * re-listing, and the expiry page says exactly that. The cost of being wrong on
 * the long side is not recoverable, because nothing can revoke a minted JWS
 * except its own `exp` (there is no per-link revocation list — the single-redeem
 * `jti` only fires once the link has been USED).
 */
export const BLOCKER_FIX_LINK_TTL_SECONDS = 60 * 60;

/**
 * The sha256 (hex) of a field-name SET — what a fix link covers.
 *
 * ORDER-INDEPENDENT: the names are sorted by UTF-16 code unit (`Array#sort`'s
 * default, never `localeCompare`, whose order depends on the host's locale), so
 * the mint and the redeem agree however either lists them. FRAMED: the digest is
 * over the JSON array, so `['AB']` and `['A','B']` cannot collide.
 *
 * THROWS on a duplicate: a list with a repeat is not a field set, and
 * `BlockerNeedsSchema` refuses one at the read, so a repeat reaching here came
 * from a caller that skipped it.
 */
export function fieldsHash(names: readonly string[]): string {
  const sorted = [...names].sort();
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1]) {
      throw new Error(`[@developerz/domain] fieldsHash: duplicate field name "${sorted[i]}"`);
    }
  }
  return nodeCrypto.createHash('sha256').update(JSON.stringify(sorted)).digest('hex');
}

/**
 * {@link fieldsHash} over a stored form's field names ({@link needsFieldNames}).
 * The ONE derivation both the mint and the redeem use: a redeem that hashed
 * `fields.map((f) => f.name)` instead would drop the `mcp:`/`oauth:` prefixes and
 * refuse every link over those kinds.
 */
export function needsFieldsHash(needs: BlockerNeeds): string {
  return fieldsHash(needsFieldNames(needs));
}

/**
 * The signed payload. Claim names follow the v1 link's: snake_case, `account` +
 * `blocker_id`, the RFC 7519 trio.
 *
 * Every claim is a piece of the capability, and there is nothing here a caller
 * supplies at redeem time except the values themselves — which is the property
 * that makes the POST handler's authorization decision a signature check plus
 * one hash compare.
 */
export const BlockerFixLinkPayloadSchema = z.object({
  /** The tenant. Every store call downstream is fenced on it. */
  account: AccountIdSchema,
  /** The blocker whose form this fills — and closes, once the form is complete. */
  blocker_id: BlockerIdSchema,
  /** {@link needsFieldsHash} of the blocker's form AT MINT. The redeem refuses a row that no longer matches. */
  fields_hash: z.string().regex(/^[0-9a-f]{64}$/, 'a sha256 hex digest'),
  /** Unix seconds (RFC 7519 NumericDate). */
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
  /** The link's own id — the key the redemption store records it under. */
  jti: z.string().min(8).max(128),
});
export type BlockerFixLinkPayload = z.infer<typeof BlockerFixLinkPayloadSchema>;

export type SignBlockerFixLinkInput = {
  account: string;
  blockerId: string;
  /** The blocker's form. Only its field NAMES reach the token, as {@link needsFieldsHash}. */
  needs: BlockerNeeds;
  /** Override the default TTL (seconds). */
  ttlSec?: number;
  /** Test seam — defaults to `Date.now`. */
  now?: () => number;
  /** Test seam — defaults to `crypto.randomUUID`. */
  newJti?: () => string;
};

/**
 * Mint the compact JWS. THROWS on anything the schema refuses and on a form with
 * a duplicate field name ({@link fieldsHash}). A minter that silently answered a
 * token our own verifier drops would hand a human a link that 400s, which is the
 * worst of both failure modes: it looks like the platform works and behaves like
 * it does not.
 */
export function signBlockerFixLink(input: SignBlockerFixLinkInput, secret: string): string {
  if (!secret) {
    throw new Error('[@developerz/domain] signBlockerFixLink: secret is empty');
  }
  const now = input.now ?? Date.now;
  const newJti = input.newJti ?? (() => nodeCrypto.randomUUID());
  const iat = Math.floor(now() / 1000);
  const payload = BlockerFixLinkPayloadSchema.parse({
    account: input.account,
    blocker_id: input.blockerId,
    fields_hash: needsFieldsHash(input.needs),
    iat,
    exp: iat + (input.ttlSec ?? BLOCKER_FIX_LINK_TTL_SECONDS),
    jti: newJti(),
  } satisfies Record<string, unknown>);

  const headerB64 = encodeHeader();
  const payloadB64 = encodeJson(payload);
  return `${headerB64}.${payloadB64}.${signSegments(headerB64, payloadB64, secret)}`;
}

export type BlockerFixLinkFailure = 'malformed' | 'bad-signature' | 'expired' | 'bad-payload';

export type BlockerFixLinkResult =
  | { ok: true; payload: BlockerFixLinkPayload }
  | { ok: false; reason: BlockerFixLinkFailure };

/**
 * Verify a fix link. Tagged result, never a throw on bad input — the four
 * failures map to four different HTTP answers and collapsing them would tell a
 * human "invalid" when the truth is "expired".
 *
 * Order: shape → signature → payload schema → expiry, so untrusted bytes are
 * rejected before anything reads payload CONTENT.
 */
export function verifyBlockerFixLink(
  token: string,
  secret: string,
  opts: { now?: () => number } = {},
): BlockerFixLinkResult {
  if (!secret) {
    throw new Error('[@developerz/domain] verifyBlockerFixLink: secret is empty');
  }
  const opened = openCompactJws(token, secret);
  if (!opened.ok) return { ok: false, reason: opened.reason };

  const parsed = BlockerFixLinkPayloadSchema.safeParse(opened.payload);
  if (!parsed.success) return { ok: false, reason: 'bad-payload' };

  const now = opts.now ?? Date.now;
  if (parsed.data.exp * 1000 <= now()) return { ok: false, reason: 'expired' };

  return { ok: true, payload: parsed.data };
}

/** The path this link is served from. One place, so the minter and the route agree. */
export const BLOCKER_FIX_PATH_PREFIX = '/blocker-fix';

/**
 * The absolute URL a human clicks.
 *
 * The token rides a PATH SEGMENT, never a query string — the rule
 * `GET /join` established when it moved its enrollment token to a header: a
 * query token leaks into access logs, proxy logs, `Referer` and shell history.
 * A path segment is not a full fix (the path is still logged by anything logging
 * request lines), it is the closest safe equivalent available to a URL a human
 * clicks, and it is why the TTL above is an hour rather than a week.
 */
export function blockerFixUrl(apiBaseUrl: string, token: string): string {
  return `${apiBaseUrl.replace(/\/+$/, '')}${BLOCKER_FIX_PATH_PREFIX}/${encodeURIComponent(token)}`;
}

/**
 * The tier a fix-link VAULT value is stored at, and it is NOT a caller's choice.
 * A constant the redeem applies, not a claim: it was never the token's to vary.
 *
 * `sealed` is the default the vault route takes when nobody says otherwise, and
 * it is the only defensible answer here: `materializable` means "a `dev` box may
 * write this to `<checkout>/.env`", which is a decision about where a credential
 * may land on a disk we do not operate. A human answering "what is the npm
 * token?" through a one-question form has not been asked that, and a form that
 * asked it would be asking the wrong person. A maintainer who wants a
 * materializable secret uses the vault page, where the choice is explicit.
 */
export const BLOCKER_FIX_LINK_EXPOSURE = CredentialExposure.Sealed;

/**
 * Why one row got no form. Every member but the last is a property of the ROW or
 * of the DEPLOYMENT — a fact a caller can act on — and `mint_threw` is the one
 * arm that stands for an error, which is why it is the only one carrying a
 * message.
 */
export type BlockerFixLinkRefusal =
  /** Nothing else is answered by storing a secret. */
  | 'not_a_credential_ask'
  /** The raiser named no fields (`needs` is NULL) — a form with no fields asks nothing. */
  | 'no_fields_named'
  /** The vault is per repo; an account-wide ask has no `(repo, environment)` to write into. */
  | 'no_repo'
  /** Handing a live write capability for a question already answered is a capability nobody asked for. */
  | 'already_settled'
  /** A vault field asks for `prod`. Policy-refused here rather than left to a later throw — see below. */
  | 'environment_not_askable'
  /** No `BLOCKER_FIX_SECRET` in this deployment, so nothing could verify the link later. */
  | 'no_secret'
  /** `signBlockerFixLink` refused its own input. Carries the message; the operator has to see it. */
  | 'mint_threw';

/** A minted link, or the reason there is none. */
export type BlockerFixLinkMint =
  | { readonly kind: 'minted'; readonly url: string }
  | { readonly kind: 'none'; readonly reason: BlockerFixLinkRefusal; readonly error?: string };

/**
 * Mint the prefilled-form URL for one row, or answer a REASON that is a property
 * of the row rather than a swallowed error.
 *
 * ONE SEAM, TWO CALLERS, and that is the whole reason this lives in `domain`
 * rather than beside either of them. It was written once in `apps/api`
 * (`deps/blockers.ts`, the `blocker list` / REST projection) and a second caller
 * arrived with plan 101 doc 07 step 3 — `apps/worker`'s blocked-signal ingest,
 * which hands the link straight back into the run that raised the ask. Apps never
 * import apps, so a second copy of "which rows deserve a form" was the only other
 * option, and two copies of a rule is how two surfaces start disagreeing about
 * whether a human can answer a question.
 *
 * SIX CONDITIONS, all of them about whether a form could do anything, and the
 * ORDER is load-bearing: every ROW property is decided BEFORE the deployment's
 * secret. `no_secret` therefore means "this row deserved a link and this
 * deployment cannot sign one" — a rare, alertable fact — instead of the answer
 * every row gets when the secret is unset, which is what a secret-first order
 * made it and which is unloggable without drowning the operator in the 80
 * `decision_needed` rows prod actually holds.
 *
 * A `prod` ask is stated as a CONDITION, and it has to be: the v2 token carries
 * no environment, so nothing downstream of this line would refuse it before a
 * human typed a production credential into the form. It is the expected, silent
 * answer for a row nobody should have written (`BlockerSchema` still READS a
 * `prod` field deliberately — narrowing a read is data loss), kept apart from
 * `mint_threw`, which is a mint that failed for a reason we did not anticipate.
 * Collapsing them logs a scary error on a case we already understand, and an
 * operator who learns to ignore that line will ignore the real one too.
 */
export function mintBlockerFixUrl(
  row: Blocker,
  cfg: { readonly secret?: string | undefined; readonly apiBaseUrl: string },
): BlockerFixLinkMint {
  if (row.kind !== BlockerKind.MissingCredential) {
    return { kind: 'none', reason: 'not_a_credential_ask' };
  }
  if (row.needs === null) return { kind: 'none', reason: 'no_fields_named' };
  if (row.repoFullName === null) return { kind: 'none', reason: 'no_repo' };
  if (row.resolution !== null) return { kind: 'none', reason: 'already_settled' };
  if (!row.needs.fields.every(isAskableField)) {
    return { kind: 'none', reason: 'environment_not_askable' };
  }
  if (!cfg.secret) return { kind: 'none', reason: 'no_secret' };

  try {
    const token = signBlockerFixLink(
      { account: row.accountId, blockerId: row.id, needs: row.needs },
      cfg.secret,
    );
    return { kind: 'minted', url: blockerFixUrl(cfg.apiBaseUrl, token) };
  } catch (err) {
    return {
      kind: 'none',
      reason: 'mint_threw',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** A vault field must ask for an askable environment; the other kinds carry none. */
function isAskableField(field: BlockerNeeds['fields'][number]): boolean {
  switch (field.kind) {
    case NeedsFieldKind.EnvSecret:
    case NeedsFieldKind.ServiceUrl:
      return isBlockerAskableEnvironment(field.environment);
    case NeedsFieldKind.McpServer:
    case NeedsFieldKind.OauthConnection:
      return true;
  }
}

/**
 * MAY THIS CALLER BE HANDED A FIX LINK? The one scope check behind every door
 * that projects one, and the answer is `write:secrets` — nothing narrower.
 *
 * THE LINK IS A WRITE CAPABILITY, and that is the whole argument. Anyone holding
 * the URL can POST values that are SEALED INTO THE REPO VAULT (`blocker-fix.ts`
 * accepts it as a bearer and submits), while `blocker submit` — the same act
 * through the front door — is gated on `write:secrets` on both transports. So a
 * projection that hands the link to a `read:tasks` caller hands them the write
 * they were refused, through a field on a read. It did: `blocker list`,
 * `blocker answer`, `blocker withdraw`, `blocker spec` and the `next_steps`
 * access forms all minted one with no knowledge of who was asking, and
 * `OWNER_ONLY_SCOPES` (`apps/api/src/middleware/scopes.ts`) holds
 * `write:secrets`, so EVERY non-owner dashboard member — viewer included — took
 * delivery of it on every list.
 *
 * IT TAKES A SCOPE SET, not a principal, because the two transports resolve
 * different caller objects over the same vocabulary: `Principal.scopes`
 * (`ReadonlySet<Scope>`) and `McpCaller.scopes` (`ReadonlySet<string>`). A
 * `ReadonlySet<Scope>` satisfies the wider parameter without conversion, so one
 * predicate serves both doors and neither can drift into its own answer.
 *
 * IT IS NOT THE MINT RULE. {@link mintBlockerFixUrl} decides which ROWS deserve a
 * form; this decides which CALLERS may be handed one. Both refusals are the same
 * `null` on the wire — the row stays answerable in prose either way — and that is
 * deliberate: a caller who may not hold the link learns nothing about whether one
 * exists.
 */
export function mayHoldBlockerFixLink(scopes: ReadonlySet<string>): boolean {
  return scopes.has(Scope.WriteSecrets);
}

/** The minter a caller who may hold no link is served: one `null` for every row. */
const NO_FIX_URL = (): null => null;

/**
 * The minter to project WITH, for one caller: the real one when
 * {@link mayHoldBlockerFixLink} admits them, and a `null` minter otherwise.
 *
 * A GATE THAT RETURNS THE MINTER rather than one each door reads as a boolean:
 * every projection already takes `fixUrl` as a parameter (`maskBlocker`,
 * `projectBlockerSpec`), so wrapping it here is the smallest change that cannot
 * be half-applied — a door that forgets the gate passes the raw minter and reads
 * wrong at a glance, where a door that forgets a boolean reads like every other
 * line. The minter itself is untouched: the projection was the defect.
 */
export function fixUrlForCaller(
  scopes: ReadonlySet<string>,
  mint: (row: Blocker) => string | null,
): (row: Blocker) => string | null {
  return mayHoldBlockerFixLink(scopes) ? mint : NO_FIX_URL;
}
